/** @jsxImportSource @emotion/react */
import React from "react";
import { css } from "@emotion/react";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Flex, Text, View, Icon, Icons, Button, Slider, NumberField, Label, Input } from "@phoenix/components";
import { useSpanFilterCondition } from "./SpanFilterConditionContext";
import { scrollbarCSS } from "./styles";

const sidebarCSS = css`
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: var(--ac-global-color-grey-50);
  border-right: 1px solid var(--ac-global-color-grey-300);
  overflow: hidden;
`;

const filterItemCSS = css`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
  &:hover {
    background-color: var(--ac-global-color-grey-100);
  }
`;

const searchInputCSS = css`
  width: 100%;
  padding: 6px 12px;
  font-size: 13px;
  border: 1px solid var(--ac-global-color-grey-300);
  border-radius: 6px;
  background-color: white;
  outline: none;
  transition: border-color 0.2s;
  &:focus {
    border-color: var(--ac-global-color-blue-500);
  }
  &::placeholder {
    color: var(--ac-global-color-grey-400);
  }
`;

const optionItemCSS = css`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.1s;
  &:hover {
    background-color: var(--ac-global-color-grey-100);
  }
`;

const FILTER_CATEGORIES = [
    "Agent Name",
    "Username",
    "Key Alias",
    "Model",
    "Session ID",
    "Latency",
    "Tokens",
    "Cost",
];

const RANGE_CONFIGS: Record<string, { min: number, max: number, step: number, unit: string, scale: number }> = {
    "Latency": { min: 0, max: 60, step: 0.1, unit: "s", scale: 1000 },
    "Tokens": { min: 0, max: 10000, step: 1, unit: "", scale: 1 },
    "Cost": { min: 0, max: 1, step: 0.01, unit: "$", scale: 1 },
};

export interface FilterSidebarProps {
    items: any[];
    excludedCategories?: string[];
    onFilterChange?: (condition: string) => void;
    filterCondition?: string;
}

const parseAttrObject = (val: any): Record<string, any> => {
    if (!val) return {};
    if (typeof val === "object") return val;
    if (typeof val === "string") {
        try {
            return JSON.parse(val);
        } catch {
            return {};
        }
    }
    return {};
};

const getDeepValue = (obj: any, keys: string[]): string | null => {
    if (!obj || typeof obj !== "object") return null;
    for (const key of keys) {
        if (obj[key] !== undefined && obj[key] !== null) return String(obj[key]);
        const parts = key.split(".");
        let curr = obj;
        let found = true;
        for (const p of parts) {
            if (curr && typeof curr === "object" && p in curr) {
                curr = curr[p];
            } else {
                found = false;
                break;
            }
        }
        if (found && curr !== undefined && curr !== null && typeof curr !== "object") {
            return String(curr);
        }
    }
    return null;
};

const extractModelFromItem = (item: any): string | null => {
    if (!item) return null;
    if (item.modelName) return String(item.modelName);

    const modelKeys = [
        "llm.model_name",
        "llm.model",
        "model_name",
        "model",
        "llm.response.model",
        "response.model",
        "gen_ai.request.model",
        "gen_ai.response.model",
        "llm_model_name",
    ];

    const attrs = parseAttrObject(item.attributes);
    const foundAttr = getDeepValue(attrs, modelKeys);
    if (foundAttr) return foundAttr;

    const meta = parseAttrObject(item.metadata);
    const foundMeta = getDeepValue(meta, modelKeys);
    if (foundMeta) return foundMeta;

    if (item.descendants?.edges) {
        for (const edge of item.descendants.edges) {
            const child = edge.node;
            if (child.modelName) return String(child.modelName);
            const childAttrs = parseAttrObject(child.attributes);
            const cFound = getDeepValue(childAttrs, modelKeys);
            if (cFound) return cFound;
        }
    }
    return null;
};

const extractKeyAliasFromItem = (item: any): string | null => {
    if (!item) return null;
    if (item.keyAlias) return String(item.keyAlias);
    const keyAliasKeys = ["user_api_key_alias", "key_alias", "user_api_key_hash"];
    const meta = parseAttrObject(item.metadata);
    const metaVal = getDeepValue(meta, keyAliasKeys);
    if (metaVal) return metaVal;
    const attrs = parseAttrObject(item.attributes);
    const attrVal = getDeepValue(attrs, keyAliasKeys);
    if (attrVal) return attrVal;
    if (item.descendants?.edges) {
        for (const edge of item.descendants.edges) {
            const child = edge.node;
            if (child.keyAlias) return String(child.keyAlias);
        }
    }
    return null;
};

export function FilterSidebar({ items, excludedCategories = [], onFilterChange, filterCondition: filterConditionProp }: FilterSidebarProps) {
    const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
    const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({});
    const [rangeFilters, setRangeFilters] = useState<Record<string, [number, number]>>({
        "Latency": [0, 60],
        "Tokens": [0, 10000],
        "Cost": [0, 1],
    });
    const [searchTerms, setSearchTerms] = useState<Record<string, string>>({});
    const { filterCondition: contextFilterCondition, setFilterCondition } = useSpanFilterCondition();
    const filterCondition = filterConditionProp !== undefined ? filterConditionProp : contextFilterCondition;

    const allOptionsRef = useRef<Record<string, Set<string>>>({
        "Agent Name": new Set(),
        "Username": new Set(),
        "Key Alias": new Set(),
        "Model": new Set(),
        "Session ID": new Set(),
    });

    const filterOptions = useMemo(() => {
        if (!filterCondition) {
            allOptionsRef.current["Agent Name"].clear();
            allOptionsRef.current["Username"].clear();
            allOptionsRef.current["Key Alias"].clear();
            allOptionsRef.current["Model"].clear();
            allOptionsRef.current["Session ID"].clear();
        }

        items.forEach(item => {
            if (item.agentName) allOptionsRef.current["Agent Name"].add(item.agentName);
            if (item.username) allOptionsRef.current["Username"].add(item.username);
            const keyAliasVal = extractKeyAliasFromItem(item);
            if (keyAliasVal) allOptionsRef.current["Key Alias"].add(keyAliasVal);
            const modelVal = extractModelFromItem(item);
            if (modelVal) allOptionsRef.current["Model"].add(modelVal);
            if (item.sessionId) allOptionsRef.current["Session ID"].add(item.sessionId);
            if (item.metadata?.session_id) allOptionsRef.current["Session ID"].add(item.metadata.session_id);
        });

        // Unconditionally add currently selected filters so they don't disappear while typing or when items shrink
        (Object.entries(selectedFilters) as [string, string[]][]).forEach(([category, options]) => {
            if (allOptionsRef.current[category]) {
                options.forEach((o: string) => allOptionsRef.current[category].add(o));
            }
        });

        return {
            "Agent Name": Array.from(allOptionsRef.current["Agent Name"]).sort(),
            "Username": Array.from(allOptionsRef.current["Username"]).sort(),
            "Key Alias": Array.from(allOptionsRef.current["Key Alias"]).sort(),
            "Model": Array.from(allOptionsRef.current["Model"]).sort(),
            "Session ID": Array.from(allOptionsRef.current["Session ID"]).sort(),
            "Latency": ["< 100ms", "100ms - 1s", "> 1s"],
            "Tokens": ["< 100", "100 - 1000", "> 1000"],
            "Cost": ["< $0.01", "$0.01 - $0.10", "> $0.10"],
        };
    }, [items, filterCondition, selectedFilters]);

    const toggleCategory = (category: string) => {
        setExpandedCategories((prev: string[]) =>
            prev.includes(category)
                ? prev.filter((c: string) => c !== category)
                : [...prev, category]
        );
    };

    const handleClearAll = useCallback(() => {
        setSelectedFilters({});
        setRangeFilters({
            "Latency": [0, 60],
            "Tokens": [0, 10000],
            "Cost": [0, 1],
        });
        setFilterCondition("");
    }, [setFilterCondition]);

    const isSidebarChange = useRef(false);
    const skipConditionUpdate = useRef(false);

    const handleCheckboxChange = (category: string, option: string) => {
        isSidebarChange.current = true;
        setSelectedFilters((prev: Record<string, string[]>) => {
            const current = prev[category] || [];
            const updated = current.includes(option)
                ? current.filter((o: string) => o !== option)
                : [...current, option];
            return { ...prev, [category]: updated };
        });
    };

    useEffect(() => {
        if (isSidebarChange.current) {
            isSidebarChange.current = false;
            return;
        }

        if (filterCondition === "") {
            setSelectedFilters({});
            return;
        }

        const nextFilters: Record<string, string[]> = {};
        const nextRangeValues: Record<string, [number, number]> = {
            "Latency": [0, 60],
            "Tokens": [0, 10000],
            "Cost": [0, 1],
        };

        const agentNameMatches = filterCondition.match(/agent_name == ['"]([^'"]+)['"]/g);
        if (agentNameMatches) {
            nextFilters["Agent Name"] = agentNameMatches.flatMap((m: string) => {
                const r = m.match(/['"]([^'"]+)['"]/);
                return r ? [r[1]] : [];
            });
        }
        const usernameMatches = filterCondition.match(/username == ['"]([^'"]+)['"]/g);
        if (usernameMatches) {
            nextFilters["Username"] = usernameMatches.flatMap((m: string) => {
                const r = m.match(/['"]([^'"]+)['"]/);
                return r ? [r[1]] : [];
            });
        }
        const keyAliasMatches = filterCondition.match(/(?:metadata\['user_api_key_alias'\]|key_alias) == ['"]([^'"]+)['"]/g);
        if (keyAliasMatches) {
            nextFilters["Key Alias"] = keyAliasMatches.flatMap((m: string) => {
                const r = m.match(/['"]([^'"]+)['"]/);
                return r ? [r[1]] : [];
            });
        }
        const modelNameMatches = filterCondition.match(/model_name == ['"]([^'"]+)['"]/g);
        if (modelNameMatches) {
            nextFilters["Model"] = modelNameMatches.flatMap((m: string) => {
                const r = m.match(/['"]([^'"]+)['"]/);
                return r ? [r[1]] : [];
            });
        }
        const sessionIdMatches = filterCondition.match(/(?:metadata\['session_id'\]|session_id) == ['"]([^'"]+)['"]/g);
        if (sessionIdMatches) {
            nextFilters["Session ID"] = sessionIdMatches.flatMap((m: string) => {
                const r = m.match(/['"]([^'"]+)['"]/);
                return r ? [r[1]] : [];
            });
        }

        const latMin = filterCondition.match(/latency_ms\s*>=\s*(\d+)/);
        const latMax = filterCondition.match(/latency_ms\s*<=\s*(\d+)/);
        if (latMin || latMax) {
            nextRangeValues["Latency"] = [
                latMin ? parseInt(latMin[1]) / 1000 : 0,
                latMax ? parseInt(latMax[1]) / 1000 : 60
            ];
        }

        const tokMin = filterCondition.match(/cumulative_token_count\.total\s*>=\s*(\d+)/);
        const tokMax = filterCondition.match(/cumulative_token_count\.total\s*<=\s*(\d+)/);
        if (tokMin || tokMax) {
            nextRangeValues["Tokens"] = [
                tokMin ? parseInt(tokMin[1]) : 0,
                tokMax ? parseInt(tokMax[1]) : 10000
            ];
        }

        const costMin = filterCondition.match(/trace\.cost\s*>=\s*([\d.]+)/);
        const costMax = filterCondition.match(/trace\.cost\s*<=\s*([\d.]+)/);
        if (costMin || costMax) {
            nextRangeValues["Cost"] = [
                costMin ? parseFloat(costMin[1]) : 0,
                costMax ? parseFloat(costMax[1]) : 1
            ];
        }

        const filtersChanged = JSON.stringify(nextFilters) !== JSON.stringify(selectedFilters);
        const rangesChanged = JSON.stringify(nextRangeValues) !== JSON.stringify(rangeFilters);

        if (filtersChanged || rangesChanged) {
            skipConditionUpdate.current = true;
            if (filtersChanged) setSelectedFilters(nextFilters);
            if (rangesChanged) setRangeFilters(nextRangeValues);
        }
    }, [filterCondition]);

    useEffect(() => {
        if (skipConditionUpdate.current) {
            skipConditionUpdate.current = false;
            return;
        }

        const sidebarConditions: string[] = [];

        Object.entries(selectedFilters).forEach(([category, options]) => {
            if (options.length === 0) return;
            if (category === "Agent Name") sidebarConditions.push(`(${options.map(o => `agent_name == '${o}'`).join(" or ")})`);
            if (category === "Username") sidebarConditions.push(`(${options.map(o => `username == '${o}'`).join(" or ")})`);
            if (category === "Key Alias") sidebarConditions.push(`(${options.map(o => `key_alias == '${o}'`).join(" or ")})`);
            if (category === "Model") sidebarConditions.push(`(${options.map(o => `model_name == '${o}'`).join(" or ")})`);
            if (category === "Session ID") sidebarConditions.push(`(${options.map(o => `session_id == '${o}'`).join(" or ")})`);
        });

        const [lMin, lMax] = rangeFilters["Latency"];
        if (lMin > 0 || lMax < 60) sidebarConditions.push(`latency_ms >= ${Math.round(lMin * 1000)} and latency_ms <= ${Math.round(lMax * 1000)}`);

        const [tMin, tMax] = rangeFilters["Tokens"];
        if (tMin > 0 || tMax < 10000) sidebarConditions.push(`cumulative_token_count.total >= ${tMin} and cumulative_token_count.total <= ${tMax}`);

        const [cMin, cMax] = rangeFilters["Cost"];
        if (cMin > 0 || cMax < 1) sidebarConditions.push(`trace.cost >= ${cMin} and trace.cost <= ${cMax}`);

        const otherConditions = filterCondition.split(/\s+and\s+/).filter(part => {
            if (!part) return false;
            return !(part.includes("agent_name ==") || part.includes("username ==") || part.includes("key_alias ==") || part.includes("model_name ==") || part.includes("session_id ==") ||
                part.includes("latency_ms") || part.includes("cumulative_token_count") || part.includes("trace.cost"));
        });

        const newCondition = [...otherConditions, ...sidebarConditions].filter(Boolean).join(" and ");
        if (newCondition !== filterCondition) {
            if (onFilterChange) onFilterChange(newCondition);
            else setFilterCondition(newCondition);
        }
    }, [selectedFilters, rangeFilters, setFilterCondition, filterCondition, onFilterChange]);

    return (
        <div css={[sidebarCSS, scrollbarCSS]}>
            <div css={css`
                flex: none;
                padding: 16px;
                border-bottom: 1px solid var(--ac-global-color-grey-300);
                background-color: var(--ac-global-color-grey-50);
                z-index: 10;
                box-shadow: 0 1px 2px rgba(0,0,0,0.05);
            `}>
                <Flex direction="row" justifyContent="space-between" alignItems="center">
                    <Text weight="heavy" size="M">Filters</Text>
                    <Button variant="quiet" size="S" onPress={handleClearAll}>
                        <Text size="S" color="blue-600" weight="heavy">Clear All</Text>
                    </Button>
                </Flex>
            </div>

            <div css={[css`flex: 1; overflow-y: auto;`, scrollbarCSS]}>
                <Flex direction="column">
                    {FILTER_CATEGORIES.filter(cat => !excludedCategories.includes(cat)).map((category) => {
                        const isExpanded = expandedCategories.includes(category);
                        const allCategoryOptions = (filterOptions as any)[category] || [];
                        const isRangeCategory = ["Latency", "Tokens", "Cost"].includes(category);
                        const rangeConfig = RANGE_CONFIGS[category];
                        const hasSelections = isRangeCategory
                            ? (rangeFilters[category][0] > rangeConfig.min || rangeFilters[category][1] < rangeConfig.max)
                            : (selectedFilters[category] || []).length > 0;
                        const searchTerm = searchTerms[category] || "";

                        const filteredOptions = allCategoryOptions.filter((opt: string) =>
                            opt.toLowerCase().includes(searchTerm.toLowerCase())
                        );

                        if (allCategoryOptions.length === 0 && !isRangeCategory) {
                            return null;
                        }

                        return (
                            <div key={category} style={{ borderBottom: "1px solid var(--ac-global-color-grey-200)" }}>
                                <div css={filterItemCSS} onClick={() => toggleCategory(category)}>
                                    <Flex direction="row" gap="size-100" alignItems="center">
                                        <Text
                                            size="S"
                                            weight={isExpanded || hasSelections ? "heavy" : "regular"}
                                            color={hasSelections ? "blue-600" : "inherit"}
                                        >
                                            {category}
                                        </Text>
                                        {hasSelections && !isRangeCategory && (
                                            <View
                                                backgroundColor="blue-500"
                                                paddingStart="size-50"
                                                paddingEnd="size-50"
                                                css={css`border-radius: 10px; min-width: 18px; height: 18px; display: flex; align-items: center; justify-content: center;`}
                                            >
                                                <Text size="XS" color="white" weight="heavy">
                                                    {selectedFilters[category].length}
                                                </Text>
                                            </View>
                                        )}
                                        {hasSelections && isRangeCategory && (
                                            <div css={css`width: 6px; height: 6px; border-radius: 50%; background-color: var(--ac-global-color-blue-500);`} />
                                        )}
                                    </Flex>
                                    <Icon
                                        svg={isExpanded ? <Icons.ChevronDown /> : <Icons.ChevronRight />}
                                        css={css`opacity: 0.5; width: 14px; height: 14px;`}
                                    />
                                </div>
                                {isExpanded && (
                                    isRangeCategory ? (
                                        <div css={css`padding: 16px; background-color: var(--ac-global-color-grey-50);`}>
                                            <Flex direction="column" gap="size-150">
                                                <Flex direction="row" gap="size-200" alignItems="center">
                                                    <Flex direction="row" alignItems="center" gap="size-50" flex={1}>
                                                        <NumberField
                                                            aria-label="Min value"
                                                            minValue={rangeConfig.min}
                                                            maxValue={rangeConfig.max}
                                                            step={rangeConfig.step}
                                                            value={rangeFilters[category][0]}
                                                            onChange={(v: number) => {
                                                                isSidebarChange.current = true;
                                                                setRangeFilters(prev => ({ ...prev, [category]: [v, prev[category][1]] }));
                                                            }}
                                                            css={css`flex: 1;`}
                                                        >
                                                            <Label css={css`font-size: 11px; font-weight: 600; margin-bottom: 4px; display: block; color: var(--ac-global-text-color-600);`}>Min.</Label>
                                                            <Input css={css`width: 100%; height: 32px; text-align: center; border-radius: 6px; border: 1px solid var(--ac-global-color-grey-300); background: white; font-size: 13px;`} />
                                                        </NumberField>
                                                        {rangeConfig.unit && <Text size="XS" color="secondary" css={css`margin-top: 18px;`}>{rangeConfig.unit}</Text>}
                                                    </Flex>
                                                    <Flex direction="row" alignItems="center" gap="size-50" flex={1}>
                                                        <NumberField
                                                            aria-label="Max value"
                                                            minValue={rangeConfig.min}
                                                            maxValue={rangeConfig.max}
                                                            step={rangeConfig.step}
                                                            value={rangeFilters[category][1]}
                                                            onChange={(v: number) => {
                                                                isSidebarChange.current = true;
                                                                setRangeFilters(prev => ({ ...prev, [category]: [prev[category][0], v] }));
                                                            }}
                                                            css={css`flex: 1;`}
                                                        >
                                                            <Label css={css`font-size: 11px; font-weight: 600; margin-bottom: 4px; display: block; color: var(--ac-global-text-color-600);`}>Max.</Label>
                                                            <Input css={css`width: 100%; height: 32px; text-align: center; border-radius: 6px; border: 1px solid var(--ac-global-color-grey-300); background: white; font-size: 13px;`} />
                                                        </NumberField>
                                                        {rangeConfig.unit && <Text size="XS" color="secondary" css={css`margin-top: 18px;`}>{rangeConfig.unit}</Text>}
                                                    </Flex>
                                                </Flex>
                                                <div css={css`padding: 8px 10px;`}>
                                                    <Slider
                                                        minValue={rangeConfig.min}
                                                        maxValue={rangeConfig.max}
                                                        step={rangeConfig.step}
                                                        value={rangeFilters[category]}
                                                        onChange={(v: any) => {
                                                            isSidebarChange.current = true;
                                                            setRangeFilters(prev => ({ ...prev, [category]: v as [number, number] }));
                                                        }}
                                                    />
                                                </div>
                                            </Flex>
                                        </div>
                                    ) : (
                                        <View padding="size-150" backgroundColor="grey-50">
                                            <Flex direction="column" gap="size-100">
                                                {allCategoryOptions.length > 5 && (
                                                    <input
                                                        css={searchInputCSS}
                                                        placeholder={`Find ${category.toLowerCase()}...`}
                                                        value={searchTerm}
                                                        onChange={(e) => setSearchTerms(prev => ({ ...prev, [category]: e.target.value }))}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                )}
                                                <div
                                                    css={[
                                                        scrollbarCSS,
                                                        css`
                                                        max-height: 200px;
                                                        overflow-y: auto;
                                                        padding-right: 4px;
                                                        display: flex;
                                                        flex-direction: column;
                                                        gap: 4px;
                                                    `
                                                    ]}
                                                >
                                                    {filteredOptions.length > 0 ? (
                                                        filteredOptions.map((option: string) => (
                                                            <div
                                                                key={option}
                                                                css={optionItemCSS}
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    handleCheckboxChange(category, option);
                                                                }}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    id={`${category}-${option}`}
                                                                    checked={(selectedFilters[category] || []).includes(option)}
                                                                    onChange={() => { }}
                                                                    style={{ cursor: 'pointer' }}
                                                                />
                                                                <label
                                                                    htmlFor={`${category}-${option}`}
                                                                    style={{
                                                                        fontSize: "13px",
                                                                        cursor: "pointer",
                                                                        color: (selectedFilters[category] || []).includes(option)
                                                                            ? "var(--ac-global-color-blue-700)"
                                                                            : "var(--ac-global-color-grey-700)",
                                                                        whiteSpace: "nowrap",
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis",
                                                                        flex: 1
                                                                    }}
                                                                >
                                                                    {option}
                                                                </label>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <Text size="XS" color="text-400" align="center" padding="size-100">
                                                            No results found
                                                        </Text>
                                                    )}
                                                </div>
                                            </Flex>
                                        </View>
                                    )
                                )}
                            </div>
                        );
                    })}
                </Flex>
            </div>
        </div>
    );
}
